import React, { useState, useEffect } from 'react';
import { FileCheck, Download, X, CheckCircle, XCircle, HelpCircle, Send, Clock, MessageSquare, ChevronRight, AlertTriangle, User, Plus, Edit3, Trash2, CheckSquare, Square, Search, Eye } from 'lucide-react';
import { api } from '../api/client';
import { useFilters } from '../context/FilterContext';
import { useAuth } from '../context/AuthContext';
import DecisionDrawer from '../components/DecisionDrawer';
import TrackingItemDetailDrawer from '../components/TrackingItemDetailDrawer';
import { ENTITY_COLORS } from '../components/EntityPicker';
import TrackingFilterBar from '../components/filters/TrackingFilterBar';

const DecisionsPage = () => {
  const { filters: globalFilters, getFilterParams } = useFilters();
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('all');
  const [decisions, setDecisions] = useState([]);
  const [steercoQueue, setSteercoQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDecision, setSelectedDecision] = useState(null);
  
  // Filter Bar 2.0 state
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [quickFilters, setQuickFilters] = useState({});
  const [filterCounts, setFilterCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  
  const [showActionModal, setShowActionModal] = useState(null); // 'approve' | 'reject' | 'needsInfo'
  const [actionNotes, setActionNotes] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingDecision, setEditingDecision] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // Detail drawer with comments
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [viewingDecision, setViewingDecision] = useState(null);

  const isSteercoMember = hasRole('sysadmin', 'pmo_power', 'program_admin');

  // Load decisions when filters change
  useEffect(() => { loadDecisions(); }, [advancedFilters, quickFilters, searchTerm]);
  useEffect(() => { 
    if (isSteercoMember) loadSteercoQueue(); 
  }, [isSteercoMember]);

  const loadDecisions = async () => {
    try {
      setLoading(true);
      
      // Build params for the new filter API
      const params = {
        // Multi-select filters
        ...(advancedFilters.status?.length > 0 && { status: advancedFilters.status.join(',') }),
        ...(advancedFilters.impact?.length > 0 && { impact: advancedFilters.impact.join(',') }),
        ...(advancedFilters.category?.length > 0 && { category: advancedFilters.category.join(',') }),
        ...(advancedFilters.escalation_level?.length > 0 && { escalation_level: advancedFilters.escalation_level.join(',') }),
        ...(advancedFilters.owner?.length > 0 && { owner: advancedFilters.owner.join(',') }),
        ...(advancedFilters.approver?.length > 0 && { approver: advancedFilters.approver.join(',') }),
        ...(advancedFilters.portfolio?.length > 0 && { portfolio: advancedFilters.portfolio.join(',') }),
        ...(advancedFilters.program?.length > 0 && { program: advancedFilters.program.join(',') }),
        ...(advancedFilters.project?.length > 0 && { project: advancedFilters.project.join(',') }),
        
        // Date ranges
        ...(advancedFilters.due_date_start && { due_date_start: advancedFilters.due_date_start }),
        ...(advancedFilters.due_date_end && { due_date_end: advancedFilters.due_date_end }),
        ...(advancedFilters.request_date_start && { request_date_start: advancedFilters.request_date_start }),
        ...(advancedFilters.request_date_end && { request_date_end: advancedFilters.request_date_end }),
        
        // Quick filters
        ...quickFilters,
        
        // Search
        ...(searchTerm && { search: searchTerm }),
        
        // Current user for "my items" filter
        ...(user?.email && { current_user: user.email }),
      };
      
      const res = await api.filterDecisions(params);
      setDecisions(res.data.decisions || []);
      setFilterCounts(res.data.counts || {});
    } catch (error) { 
      console.error('Failed to load decisions:', error); 
      // Fallback to governance API
      try {
        const res = await api.listDecisions({});
        setDecisions(res.data?.data?.decisions || []);
      } catch (e) {
        setDecisions([]);
      }
    }
    finally { setLoading(false); }
  };

  const handleCreateNew = () => {
    setEditingDecision(null);
    setDrawerOpen(true);
  };

  const handleEdit = (decision) => {
    setEditingDecision(decision);
    setDrawerOpen(true);
  };

  const handleDrawerSuccess = () => {
    loadDecisions();
    loadSteercoQueue();
    setSelectedDecision(null);
  };

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === decisions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(decisions.map(d => d.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length || !window.confirm(`Delete ${selectedIds.length} selected decisions?`)) return;
    setBulkLoading(true);
    try {
      await api.bulkDeleteDecisions(selectedIds);
      setSelectedIds([]);
      loadDecisions();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete decisions');
    }
    setBulkLoading(false);
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (!selectedIds.length) return;
    setBulkLoading(true);
    try {
      await api.bulkUpdateDecisionsStatus(selectedIds, newStatus);
      setSelectedIds([]);
      loadDecisions();
    } catch (error) {
      console.error('Bulk status update failed:', error);
      alert('Failed to update status');
    }
    setBulkLoading(false);
  };

  const loadSteercoQueue = async () => {
    try {
      const res = await api.getSteercoQueue();
      setSteercoQueue(res.data.queue || []);
    } catch (error) { console.error('Failed to load steerco queue:', error); }
  };

  const loadDecisionComments = async (decisionId) => {
    try {
      const res = await api.getDecisionComments(decisionId);
      setComments(res.data.comments || []);
    } catch (error) { console.error('Failed to load comments:', error); }
  };

  const handleSelectDecision = (decision) => {
    setSelectedDecision(decision);
    loadDecisionComments(decision.id);
  };

  const handleSubmitDecision = async (decisionId) => {
    try {
      await api.submitDecision(decisionId, { user_id: user?.id });
      loadDecisions();
      loadSteercoQueue();
      alert('Decision submitted for review');
    } catch (error) {
      alert('Failed to submit: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleEscalateDecision = async (decisionId) => {
    try {
      await api.escalateDecision(decisionId, { user_id: user?.id });
      loadDecisions();
      loadSteercoQueue();
      alert('Decision escalated to Steerco');
    } catch (error) {
      alert('Failed to escalate: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleApproveDecision = async () => {
    if (!selectedDecision) return;
    try {
      await api.approveDecision(selectedDecision.id, { 
        user_id: user?.id,
        user_name: user?.name,
        notes: actionNotes 
      });
      setShowActionModal(null);
      setActionNotes('');
      setSelectedDecision(null);
      loadDecisions();
      loadSteercoQueue();
      alert('Decision approved');
    } catch (error) {
      alert('Failed to approve: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleRejectDecision = async () => {
    if (!selectedDecision || !actionNotes.trim()) {
      alert('Please provide a rejection reason');
      return;
    }
    try {
      await api.rejectDecision(selectedDecision.id, { 
        user_id: user?.id,
        user_name: user?.name,
        reason: actionNotes 
      });
      setShowActionModal(null);
      setActionNotes('');
      setSelectedDecision(null);
      loadDecisions();
      loadSteercoQueue();
      alert('Decision rejected');
    } catch (error) {
      alert('Failed to reject: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleRequestInfo = async () => {
    if (!selectedDecision || !actionNotes.trim()) {
      alert('Please provide a question');
      return;
    }
    try {
      await api.requestDecisionInfo(selectedDecision.id, { 
        user_id: user?.id,
        user_name: user?.name,
        question: actionNotes 
      });
      setShowActionModal(null);
      setActionNotes('');
      loadDecisionComments(selectedDecision.id);
      loadDecisions();
      loadSteercoQueue();
      alert('Information requested');
    } catch (error) {
      alert('Failed to request info: ' + error.response?.data?.detail || error.message);
    }
  };

  const handleAddComment = async () => {
    if (!selectedDecision || !newComment.trim()) return;
    try {
      await api.addDecisionComment(selectedDecision.id, {
        user_id: user?.id,
        user_name: user?.name,
        comment: newComment
      });
      setNewComment('');
      loadDecisionComments(selectedDecision.id);
    } catch (error) {
      alert('Failed to add comment');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'approved': 'bg-green-100 text-green-700',
      'rejected': 'bg-red-100 text-red-700',
      'draft': 'bg-[var(--clr-surface-subtle)] text-gray-700',
      'submitted': 'bg-blue-100 text-blue-700',
      'steerco_review': 'bg-purple-100 text-purple-700',
      'needs_info': 'bg-amber-100 text-amber-700',
      'Approved': 'bg-green-100 text-green-700',
      'Rejected': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Awaiting Approval': 'bg-blue-100 text-blue-700',
    };
    return colors[status] || 'bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]';
  };

  const getStateLabel = (state) => {
    const labels = {
      'draft': 'Draft',
      'submitted': 'Submitted',
      'steerco_review': 'Steerco Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'needs_info': 'Needs Info'
    };
    return labels[state] || state;
  };

  const kpis = {
    total: decisions.length,
    pending: filterCounts.pending || decisions.filter(d => ['Pending', 'Awaiting Approval', 'submitted', 'steerco_review'].includes(d.status || d.state)).length,
    approved: decisions.filter(d => d.status === 'Approved' || d.state === 'approved').length,
    rejected: decisions.filter(d => d.status === 'Rejected' || d.state === 'rejected').length,
    steercoQueue: steercoQueue.length,
    highImpact: filterCounts.high_impact || 0,
    overdue: filterCounts.overdue || 0
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <FileCheck className="w-7 h-7 text-purple-600" />
            Decisions
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Track and approve decisions across portfolios</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreateNew}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
            data-testid="new-decision-btn"
          >
            <Plus className="w-4 h-4" /> New Decision
          </button>
          <a href={api.exportCSV('decisions', advancedFilters)} className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-surface-subtle)] hover:bg-gray-200 rounded-lg text-sm font-medium" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </a>
        </div>
      </div>

      {/* Filter Bar 2.0 */}
      <TrackingFilterBar
        trackingType="decisions"
        filters={advancedFilters}
        onFiltersChange={setAdvancedFilters}
        quickFilters={quickFilters}
        onQuickFiltersChange={setQuickFilters}
        counts={filterCounts}
        currentUser={user?.email}
        showInsightsBar={true}
        className="mb-6"
      />

      {/* Search Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search decisions by title, description, or name..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
            data-testid="decisions-search"
          />
        </div>
      </div>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Total Decisions</p>
          <p className="text-2xl font-bold">{kpis.total}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Pending Review</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-atrisk)]">{kpis.pending}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">High Impact</p>
          <p className="text-2xl font-bold text-red-600">{kpis.highImpact}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Approved</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-healthy)]">{kpis.approved}</p>
        </div>
        <div className="bg-[var(--clr-surface-card)] rounded-lg border p-4">
          <p className="text-sm text-[var(--clr-text-secondary)] mb-1">Overdue</p>
          <p className="text-2xl font-bold text-[var(--clr-sev-blocked)]">{kpis.overdue}</p>
        </div>
        {isSteercoMember && (
          <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
            <p className="text-sm text-purple-600 mb-1">Steerco Queue</p>
            <p className="text-2xl font-bold text-purple-700">{kpis.steercoQueue}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      {isSteercoMember && (
        <div className="border-b mb-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === 'all' ? 'border-purple-600 text-purple-600' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
              data-testid="tab-all-decisions"
            >
              All Decisions
            </button>
            <button
              onClick={() => setActiveTab('steerco')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'steerco' ? 'border-purple-600 text-purple-600' : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'}`}
              data-testid="tab-steerco-queue"
            >
              <AlertTriangle className="w-4 h-4" />
              Steerco Queue
              {steercoQueue.length > 0 && (
                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                  {steercoQueue.length}
                </span>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Steerco Queue Tab */}
      {activeTab === 'steerco' && isSteercoMember && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-purple-900">Decisions Awaiting Your Review</h3>
            <p className="text-sm text-purple-700 mt-1">Review and take action on escalated decisions. You can approve, reject, or request more information.</p>
          </div>

          {steercoQueue.length === 0 ? (
            <div className="bg-[var(--clr-surface-card)] rounded-lg border p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-[var(--clr-text-secondary)]">No decisions awaiting review</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {steercoQueue.map(decision => (
                <div 
                  key={decision.id} 
                  className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleSelectDecision(decision)}
                  data-testid={`steerco-decision-${decision.id}`}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(decision.state || decision.status)}`}>
                            {getStateLabel(decision.state) || decision.status}
                          </span>
                          {decision.program_name && (
                            <span className="text-xs text-[var(--clr-text-muted)]">{decision.program_name}</span>
                          )}
                          {decision.project_name && (
                            <span className="text-xs text-[var(--clr-text-muted)]">/ {decision.project_name}</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-[var(--clr-text-primary)]">{decision.name || decision.title}</h3>
                        <p className="text-sm text-[var(--clr-text-secondary)] mt-1 line-clamp-2">{decision.description}</p>
                        
                        {decision.escalated_at && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-[var(--clr-text-muted)]">
                            <Clock className="w-3 h-3" />
                            Escalated {new Date(decision.escalated_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-[var(--clr-text-muted)]" />
                    </div>
                  </div>
                  <div className="px-4 py-3 bg-[var(--clr-surface-subtle)] border-t flex items-center justify-end gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSelectDecision(decision); setShowActionModal('approve'); }}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 flex items-center gap-1"
                      data-testid={`approve-${decision.id}`}
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSelectDecision(decision); setShowActionModal('reject'); }}
                      className="px-3 py-1.5 bg-[var(--clr-sev-blocked)] text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1"
                      data-testid={`reject-${decision.id}`}
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSelectDecision(decision); setShowActionModal('needsInfo'); }}
                      className="px-3 py-1.5 bg-[var(--clr-sev-atrisk)] text-white text-sm rounded-lg hover:bg-amber-600 flex items-center gap-1"
                      data-testid={`needs-info-${decision.id}`}
                    >
                      <HelpCircle className="w-4 h-4" /> Need Info
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Decisions Tab */}
      {activeTab === 'all' && (
        <>

          {/* Bulk Actions Toolbar */}
          {selectedIds.length > 0 && (
            <div className="bg-[var(--clr-brand-primary)] text-white px-4 py-3 rounded-lg mb-4 flex items-center justify-between" data-testid="bulk-actions-toolbar">
              <span className="font-medium">{selectedIds.length} selected</span>
              <div className="flex items-center gap-3">
                <select 
                  onChange={(e) => e.target.value && handleBulkStatusUpdate(e.target.value)}
                  className="text-sm bg-white text-gray-800 border rounded px-3 py-1.5"
                  disabled={bulkLoading}
                  data-testid="bulk-status-select"
                >
                  <option value="">Set Status...</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button 
                  onClick={handleBulkDelete}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                  disabled={bulkLoading}
                  data-testid="bulk-delete-btn"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
                <button 
                  onClick={() => setSelectedIds([])}
                  className="px-3 py-1.5 bg-white/20 text-sm rounded hover:bg-white/30"
                  data-testid="bulk-clear-btn"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* Decisions Table */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <button onClick={toggleSelectAll} className="text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]" data-testid="select-all-checkbox">
                      {selectedIds.length === decisions.length && decisions.length > 0 ? (
                        <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Decision</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Project</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Linked</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Owner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Due Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">Loading...</td></tr>
                ) : decisions.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">No decisions found</td></tr>
                ) : (
                  decisions.map(decision => (
                    <tr 
                      key={decision.id} 
                      className={`hover:bg-[var(--clr-surface-hover)] cursor-pointer ${selectedIds.includes(decision.id) ? 'bg-blue-50' : ''}`}
                      onClick={() => handleSelectDecision(decision)}
                      data-testid={`decision-row-${decision.id}`}
                    >
                      <td className="px-4 py-3">
                        <button 
                          onClick={(e) => { e.stopPropagation(); toggleSelect(decision.id); }}
                          className="text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)]"
                          data-testid={`select-decision-${decision.id}`}
                        >
                          {selectedIds.includes(decision.id) ? (
                            <CheckSquare className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-[var(--clr-text-primary)]">{decision.name || decision.title}</p>
                        <p className="text-xs text-[var(--clr-text-muted)] truncate max-w-xs">{decision.description}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{decision.project_name || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(decision.state || decision.status)}`}>
                          {getStateLabel(decision.state) || decision.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {decision.links && decision.links.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {decision.links.slice(0, 2).map((link, idx) => (
                              <span 
                                key={idx} 
                                className={`px-1.5 py-0.5 text-xs rounded ${ENTITY_COLORS[link.entity_type] || 'bg-gray-100 text-gray-700'}`}
                                title={`${link.entity_type}: ${link.display}`}
                              >
                                {link.entity_type?.slice(0,3)}
                              </span>
                            ))}
                            {decision.links.length > 2 && (
                              <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                +{decision.links.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-[var(--clr-text-muted)]">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">{decision.owner || decision.requestor || '-'}</td>
                      <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">
                        {decision.due_date ? new Date(decision.due_date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); setViewingDecision(decision); setDetailDrawerOpen(true); }}
                            className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                            data-testid={`view-decision-${decision.id}`}
                            title="View Details & Comments"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(decision); }}
                            className="p-1.5 hover:bg-gray-100 rounded text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)]"
                            data-testid={`edit-decision-${decision.id}`}
                            title="Edit"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          {(decision.state === 'draft' || decision.status === 'Pending') && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSubmitDecision(decision.id); }}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              data-testid={`submit-${decision.id}`}
                            >
                              <Send className="w-3 h-3 inline mr-1" />Submit
                            </button>
                          )}
                          {decision.state === 'submitted' && isSteercoMember && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEscalateDecision(decision.id); }}
                              className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                              data-testid={`escalate-${decision.id}`}
                            >
                              Escalate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Decision Detail Drawer */}
      {selectedDecision && !showActionModal && (
        <div className="fixed inset-0 z-50" onClick={() => setSelectedDecision(null)}>
          <div className="fixed inset-0 bg-black/50" />
          <div 
            className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-2xl overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Decision Details</h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => { setSelectedDecision(null); handleEdit(selectedDecision); }}
                  className="px-3 py-1.5 bg-[var(--clr-brand-primary)] text-white rounded-lg text-sm hover:bg-[var(--clr-brand-primary-hover)] flex items-center gap-1"
                  data-testid="edit-decision-from-drawer"
                >
                  <Edit3 className="w-4 h-4" /> Edit
                </button>
                <button onClick={() => setSelectedDecision(null)} className="p-1 hover:bg-[var(--clr-surface-hover)] rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Header */}
              <div>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(selectedDecision.state || selectedDecision.status)}`}>
                  {getStateLabel(selectedDecision.state) || selectedDecision.status}
                </span>
                <h3 className="text-xl font-bold text-[var(--clr-text-primary)] mt-2">{selectedDecision.name || selectedDecision.title}</h3>
                <p className="text-[var(--clr-text-secondary)] mt-2">{selectedDecision.description}</p>
              </div>

              {/* Linked Entities */}
              {selectedDecision.links && selectedDecision.links.length > 0 && (
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)] mb-2">Linked Entities</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedDecision.links.map((link, idx) => (
                      <span 
                        key={idx}
                        className={`px-2 py-1 text-xs rounded-lg border ${ENTITY_COLORS[link.entity_type] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                      >
                        <span className="font-medium capitalize">{link.entity_type}:</span> {link.display}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)]">Project</p>
                  <p className="font-medium text-[var(--clr-text-primary)]">{selectedDecision.project_name || '-'}</p>
                </div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)]">Owner</p>
                  <p className="font-medium text-[var(--clr-text-primary)]">{selectedDecision.owner || '-'}</p>
                </div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)]">Due Date</p>
                  <p className="font-medium text-[var(--clr-text-primary)]">
                    {selectedDecision.due_date ? new Date(selectedDecision.due_date).toLocaleDateString() : '-'}
                  </p>
                </div>
                <div className="bg-[var(--clr-surface-subtle)] rounded-lg p-3">
                  <p className="text-xs text-[var(--clr-text-muted)]">Impact</p>
                  <p className="font-medium text-[var(--clr-text-primary)]">{selectedDecision.impact || 'Medium'}</p>
                </div>
              </div>

              {/* Steerco Actions */}
              {selectedDecision.state === 'steerco_review' && isSteercoMember && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-3">Steerco Actions</h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowActionModal('approve')}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button
                      onClick={() => setShowActionModal('reject')}
                      className="flex-1 px-4 py-2 bg-[var(--clr-sev-blocked)] text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                    <button
                      onClick={() => setShowActionModal('needsInfo')}
                      className="flex-1 px-4 py-2 bg-[var(--clr-sev-atrisk)] text-white rounded-lg hover:bg-amber-600 flex items-center justify-center gap-2"
                    >
                      <HelpCircle className="w-4 h-4" /> Need Info
                    </button>
                  </div>
                </div>
              )}

              {/* Comments Section */}
              <div>
                <h4 className="font-medium text-[var(--clr-text-primary)] mb-3 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comments & Audit Trail
                </h4>
                
                {/* Add Comment */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 px-3 py-2 border rounded-lg text-sm"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                  >
                    Post
                  </button>
                </div>

                {/* Comments List */}
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {comments.length === 0 ? (
                    <p className="text-sm text-[var(--clr-text-muted)] text-center py-4">No comments yet</p>
                  ) : (
                    comments.map(comment => (
                      <div key={comment.id} className={`p-3 rounded-lg ${
                        comment.action === 'approved' ? 'bg-[var(--clr-sev-healthy)] bg-opacity-10 border border-green-200' :
                        comment.action === 'rejected' ? 'bg-[var(--clr-sev-blocked)] bg-opacity-10 border border-red-200' :
                        comment.action === 'needs_info' ? 'bg-[var(--clr-sev-atrisk)] bg-opacity-10 border border-amber-200' :
                        'bg-[var(--clr-surface-subtle)]'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-[var(--clr-text-muted)]" />
                            <span className="text-sm font-medium text-[var(--clr-text-primary)]">{comment.user_name}</span>
                            {comment.action && comment.action !== 'comment' && (
                              <span className={`px-1.5 py-0.5 text-xs rounded ${
                                comment.action === 'approved' ? 'bg-green-200 text-green-700' :
                                comment.action === 'rejected' ? 'bg-red-200 text-red-700' :
                                'bg-amber-200 text-amber-700'
                              }`}>
                                {comment.action}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-[var(--clr-text-muted)]">
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.comment}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modals */}
      {showActionModal && selectedDecision && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowActionModal(null)} />
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-2xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">
              {showActionModal === 'approve' && 'Approve Decision'}
              {showActionModal === 'reject' && 'Reject Decision'}
              {showActionModal === 'needsInfo' && 'Request More Information'}
            </h2>
            
            <p className="text-sm text-[var(--clr-text-secondary)] mb-4">
              <strong>{selectedDecision.name || selectedDecision.title}</strong>
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {showActionModal === 'approve' && 'Approval Notes (optional)'}
                {showActionModal === 'reject' && 'Rejection Reason (required)'}
                {showActionModal === 'needsInfo' && 'Question (required)'}
              </label>
              <textarea
                value={actionNotes}
                onChange={(e) => setActionNotes(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg h-24"
                placeholder={
                  showActionModal === 'approve' ? 'Add any notes for the approval...' :
                  showActionModal === 'reject' ? 'Explain why this decision is being rejected...' :
                  'What information do you need?'
                }
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowActionModal(null); setActionNotes(''); }}
                className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={
                  showActionModal === 'approve' ? handleApproveDecision :
                  showActionModal === 'reject' ? handleRejectDecision :
                  handleRequestInfo
                }
                className={`px-4 py-2 text-white rounded-lg ${
                  showActionModal === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                  showActionModal === 'reject' ? 'bg-[var(--clr-sev-blocked)] hover:bg-red-700' :
                  'bg-[var(--clr-sev-atrisk)] hover:bg-amber-600'
                }`}
              >
                {showActionModal === 'approve' && 'Approve'}
                {showActionModal === 'reject' && 'Reject'}
                {showActionModal === 'needsInfo' && 'Request Info'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision Create/Edit Drawer */}
      <DecisionDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        decision={editingDecision}
        onSuccess={handleDrawerSuccess}
      />

      {/* Detail Drawer with Comments */}
      <TrackingItemDetailDrawer
        isOpen={detailDrawerOpen}
        onClose={() => { setDetailDrawerOpen(false); setViewingDecision(null); }}
        item={viewingDecision}
        itemType="decision"
        tenantId={user?.tenant?.id || user?.tenant_id}
      />
    </div>
  );
};

export default DecisionsPage;
